import React, { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { UserForm } from '../../modules/forms/models/UserForm'
import { navigate } from '@reach/router'
import { ROUTE_FORM_GENERATOR } from '../../routes/routes-constants'
import {
  FORM_SERVICE_KEY,
  IFormService,
  IUserFormService,
  USER_FORM_SERVICE_KEY,
} from '../../modules/forms'
import { getFormContainer } from '../../container/form-module'
import { Query, QueryParam } from '../../common/api/Query'
import style from '../../components/loading-spinner/LoadingSpinner.module.css'
import { LoadingSpinner } from 'components/loading-spinner/LoadingSpinner'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { emptyFormDTO, FormDTO, fromModel } from '../../modules/forms/models/FormDTO'
import { getUserContainer } from 'container/user-module'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from 'modules/users'
import { User, UserQuery } from 'modules/users/models/User'
import { Pagination } from '../../components/table/Pagination'
import { AppTable } from '../../components/table/AppTable'
import { Field } from '../../components/table/types'
import { UserFormValue } from 'modules/forms/models/UserFormValue'
import { FormFieldType } from '../../modules/forms/enums/FormFieldType'
import { PageLayout } from 'pages/layout'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import styles from './TableResults.module.css'

type FormGeneratorResultsProps = {
  id?: string
}

type TableRowData = {
  id: string
  userName: string
  submissionDate: string
  formValues: { [key: string]: string }
  userFormId: string
  [key: string]: any
}

const formContainer = getFormContainer()
const formService = formContainer.get<IFormService>(FORM_SERVICE_KEY)
const userFormService = formContainer.get<IUserFormService>(USER_FORM_SERVICE_KEY)
const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)

export function TableResults(props: FormGeneratorResultsProps) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [page, setPage] = useState<number>(0)
  const [count, setCount] = useState<number>(0)
  const [form, setForm] = useState<FormDTO>(emptyFormDTO())
  const [tableData, setTableData] = useState<TableRowData[]>([])
  const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map())
  const usersMapRef = React.useRef<Map<string, User>>(new Map())
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [hasResponses, setHasResponses] = useState<boolean>(true)
  const [patientIDs, setPatientIDs] = useState<string[]>([])

  useEffect(() => {
    if (props.id) {
      formService.getByID(props.id).subscribe((res) => {
        if (res) {
          setForm(fromModel(res))
        }
      })
    }
  }, [props.id])

  useEffect(() => {
    const loggedUser = loggedUserService.get()
    if (!loggedUser) return

    userService
      .getFilteredList(
        new Query<UserQuery>({
          query: [{ name: 'assignedID', value: loggedUser.id }],
          pager: { offset: 0, limit: 1000 },
        })
      )
      .subscribe((res) => {
        if (res) {
          const ids = res.items.map((u) => u.id)
          setPatientIDs(ids)

          const map = new Map<string, User>()
          res.items.forEach((u) => map.set(u.id, u))
          usersMapRef.current = map
          setUsersMap(map)
        }
      })
  }, [])

  useEffect(() => {
    if (!props.id || patientIDs.length === 0) return

    setIsLoading(true)

    userFormService
      .getFilteredList(
        new Query({
          pager: { offset: page * rowsPerPage, limit: rowsPerPage },
          query: [
            new QueryParam<UserForm>('form_id', props.id),
            { name: 'user_ids', value: patientIDs },
          ],
          sort: [{ field: 'form_id' }],
        })
      )
      .subscribe((res) => {
        setHasResponses(res.items.length > 0)

        const missingIDs = res.items
          .map((item) => item.userID)
          .filter(
            (uid, index, self) => !usersMapRef.current.has(uid) && self.indexOf(uid) === index
          )

        const uniqueMissingIDs = missingIDs

        const processAndSet = (map: Map<string, User>) => {
          const processedData: TableRowData[] = []

          res.items.forEach((userForm) => {
            const dateGroups = new Map<string, UserFormValue[]>()

            userForm.userFormValues.forEach((formValue) => {
              const dateKey = formValue.date.toString()
              if (!dateGroups.has(dateKey)) {
                dateGroups.set(dateKey, [])
              }
              dateGroups.get(dateKey)!.push(formValue)
            })

            dateGroups.forEach((values, dateKey) => {
              const formValues: { [key: string]: string } = {}

              values.forEach((formValue) => {
                const fieldId = formValue.formFieldID
                const vals = formValue.getValues()

                if (vals && Array.isArray(vals) && vals.length > 0) {
                  formValues[fieldId] = vals.join(', ')
                } else {
                  formValues[fieldId] = t('notComplete')
                }
              })

              processedData.push({
                id: `${userForm.id || ''}-${dateKey}`,
                userName: userForm.userID,
                submissionDate: dateKey,
                formValues: formValues,
                userFormId: userForm.id || '',
              })
            })
          })

          setTableData(processedData)
          setCount(res.count)

          setTimeout(() => {
            setIsLoading(false)
          }, 300)
        }

        if (uniqueMissingIDs.length > 0) {
          userService
            .getFilteredList(new Query({ query: [{ name: 'ids', value: uniqueMissingIDs }] }))
            .subscribe((userRes) => {
              const newMap = new Map(usersMapRef.current)
              userRes?.items.forEach((u) => newMap.set(u.id, u))
              usersMapRef.current = newMap
              setUsersMap(newMap)
              processAndSet(newMap)
            })
        } else {
          processAndSet(usersMap)
        }
      })
  }, [props.id, page, rowsPerPage, patientIDs, t])

  const formatDateValue = (value: string, fieldType?: string | number): string => {
    const type = typeof fieldType === 'string' ? parseInt(fieldType) : fieldType

    const nonDateTypes = [
      FormFieldType.Number,
      FormFieldType.RadioButton,
      FormFieldType.Select,
      FormFieldType.MultiSelect,
      FormFieldType.CheckBox,
      FormFieldType.Text,
      FormFieldType.TextArea,
    ]

    if (nonDateTypes.includes(type as FormFieldType)) {
      return value
    }

    const isPureNumber = /^\d+$/.test(value)
    if (isPureNumber) {
      return value
    }

    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
    if (isoDateRegex.test(value)) {
      const date = new Date(value)

      if (type === FormFieldType.Time) {
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        return `${hours}:${minutes}`
      }

      if (type === FormFieldType.Date) {
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${day}/${month}/${year}`
      }

      if (type === FormFieldType.DateTime) {
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${day}/${month}/${year} ${hours}:${minutes}`
      }
    }

    const isDateType =
      type === FormFieldType.Date || type === FormFieldType.Time || type === FormFieldType.DateTime

    if (isDateType) {
      const simpleDateRegex = /^\d{4}-\d{2}-\d{2}$/
      if (simpleDateRegex.test(value)) {
        const [year, month, day] = value.split('-')
        return `${day}/${month}/${year}`
      }

      const dateObj = new Date(value)
      if (!isNaN(dateObj.getTime())) {
        if (type === FormFieldType.Time) {
          const hours = dateObj.getHours().toString().padStart(2, '0')
          const minutes = dateObj.getMinutes().toString().padStart(2, '0')
          return `${hours}:${minutes}`
        }

        if (type === FormFieldType.Date) {
          const day = dateObj.getDate().toString().padStart(2, '0')
          const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
          const year = dateObj.getFullYear()
          return `${day}/${month}/${year}`
        }

        if (type === FormFieldType.DateTime) {
          const hours = dateObj.getHours().toString().padStart(2, '0')
          const minutes = dateObj.getMinutes().toString().padStart(2, '0')
          const day = dateObj.getDate().toString().padStart(2, '0')
          const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
          const year = dateObj.getFullYear()
          return `${day}/${month}/${year} ${hours}:${minutes}`
        }
      }
    }

    return value
  }
  const renderValue = (value: string, formField?: any) => {
    if (value === 'true') return t('yes')
    if (value === 'false') return t('no')
    if (value === t('notComplete')) return value

    if (formField?.type !== undefined) {
      const formattedDate = formatDateValue(value, formField.type)
      if (formattedDate !== value) {
        return formattedDate
      }
    }

    const translatedValue = t(value)
    return translatedValue !== value ? translatedValue : value
  }

  const fields: Field<TableRowData>[] = [
    {
      name: 'userName',
      label: t('user'),
      sortable: false,
      renderFunc: (field, item) => {
        const user = usersMap.get(item.userName)
        if (user) {
          return `${user.firstName} ${user.lastName || ''}`.trim()
        }
        return item.userName
      },
    },
    {
      name: 'submissionDate',
      label: t('date'),
      sortable: false,
      renderFunc: (field, item) => {
        return new Date(item.submissionDate).toLocaleDateString('es-ES')
      },
    },
    ...form.formFields.map((formField) => ({
      name: formField.id as keyof TableRowData,
      label: formField.title ? t(formField.title) : '',
      sortable: false,
      renderFunc: (field: any, item: TableRowData) => {
        const value = item.formValues[formField.id]
        return value ? renderValue(value, formField) : t('notComplete')
      },
    })),
  ]

  const handlePaginationChange = (e: unknown, value: number) => setPage(value)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value)
    if (!Number.isNaN(value)) {
      setRowsPerPage(value)
      setPage(0)
    }
  }

  const goBack = () => navigate(ROUTE_FORM_GENERATOR)

  return (
    <PageLayout>
      {!isLoading ? (
        <Box className={styles.container}>
          <Box className={styles.header}>
            <Typography variant="h5" component="h1" className={styles.title}>
              {form.title ? t(form.title) : ''}
            </Typography>
            <AppButton
              theme={ButtonTheme.NewSecondary}
              type={'button'}
              label={t('back')}
              handler={goBack}
            />
          </Box>

          {!hasResponses ? (
            <Box className={styles.empty}>{t('noResponsesYet')}</Box>
          ) : (
            <>
              <Box className={styles.tableWrapper}>
                <AppTable
                  fields={fields}
                  items={tableData}
                  rowKeyField="id"
                  isLoading={isLoading}
                />
              </Box>

              {count > 0 && (
                <Box className={styles.pagination}>
                  <Pagination
                    page={page}
                    count={count}
                    handleChangePage={handlePaginationChange}
                    rowsPerPage={rowsPerPage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      ) : (
        <LoadingSpinner className={style.loadingSpinner} />
      )}
    </PageLayout>
  )
}
